import React, {useCallback, useEffect, useRef, useState} from "react";
import {Box, CircularProgress, IconButton, Typography} from "@mui/material";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {useTranslation} from "react-i18next";
import {
    ACCEPTED_IMAGE_MIME,
    buildImageUrl,
    isAcceptedImageFile,
    uploadTransactionImage,
} from "../../services/ImageUploadService";
import ImagePreviewModal from "./ImagePreviewModal";

/**
 * Horizontal strip of photo thumbnails for a transaction form.
 *
 * Differences from the iOS / Android editors (see docs/transaction-images-ui.md):
 *   - We DO NOT compress on the client. Each picked file is uploaded to the
 *     server immediately; the server resizes / re-encodes and gives us back
 *     `{ id, guid, fileName }`.
 *   - Thumbnails are rendered straight from the server URL (`/api/image/{id}/{name}`).
 *   - "Pending" state (uploading) is shown as a spinner over a placeholder tile.
 *
 * The list of attached images is owned by the parent (controlled component):
 *   value: Array<{ id, guid, fileName }>
 *   onChange: (next: Array<{ id, guid, fileName }>) => void
 *
 * Removing a thumbnail just strips it from `value`; the actual `Image` row on
 * the server stays orphaned (cleanup is server-side and out of scope here, same
 * contract as mobile clients).
 */
const TransactionImagesEditor = ({value = [], onChange}) => {
    const {t} = useTranslation();
    const fileInputRef = useRef(null);

    // tickets are local entries representing files mid-upload (no id yet).
    // After upload they are removed and the resulting image is appended to `value`.
    const [pendingTickets, setPendingTickets] = useState([]);
    const [errorMsg, setErrorMsg] = useState(null);
    const [previewSrc, setPreviewSrc] = useState(null);
    const [previewIndex, setPreviewIndex] = useState(-1);

    useEffect(() => {
        // Auto-clear inline error after a short delay so the strip doesn't carry stale red text.
        if (!errorMsg) return undefined;
        const id = setTimeout(() => setErrorMsg(null), 3500);
        return () => clearTimeout(id);
    }, [errorMsg]);

    const triggerFilePick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const uploadFiles = useCallback(async (filesArr) => {
        const accepted = filesArr.filter(isAcceptedImageFile);
        const rejectedCount = filesArr.length - accepted.length;
        if (rejectedCount > 0) {
            setErrorMsg(t("forms.transaction.images.unsupportedFormat"));
        }
        if (accepted.length === 0) return;

        const tickets = accepted.map((file) => ({
            ticketId: `tkt_${Date.now()}_${Math.random().toString(36).slice(2)}`,
            fileName: file.name,
        }));
        setPendingTickets((prev) => [...prev, ...tickets]);

        const uploaded = [];
        for (let i = 0; i < accepted.length; i++) {
            const file = accepted[i];
            const ticket = tickets[i];
            try {
                const result = await uploadTransactionImage(file);
                uploaded.push(result);
            } catch (err) {
                console.error("Image upload failed", err);
                setErrorMsg(t("forms.transaction.images.uploadFailed"));
            } finally {
                setPendingTickets((prev) => prev.filter((p) => p.ticketId !== ticket.ticketId));
            }
        }
        if (uploaded.length > 0 && onChange) {
            onChange([...(value || []), ...uploaded]);
        }
    }, [onChange, t, value]);

    const handleFileInputChange = (e) => {
        const files = Array.from(e.target.files || []);
        // Reset input so picking the same file again still fires `change`.
        e.target.value = "";
        uploadFiles(files);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const files = Array.from(e.dataTransfer?.files || []);
        if (files.length > 0) uploadFiles(files);
    };

    const handlePaste = (e) => {
        const items = Array.from(e.clipboardData?.items || []);
        const files = items
            .filter((it) => it.kind === "file")
            .map((it) => it.getAsFile())
            .filter(Boolean);
        if (files.length > 0) {
            e.preventDefault();
            uploadFiles(files);
        }
    };

    const handleRemove = (id) => {
        if (!onChange) return;
        onChange((value || []).filter((img) => img.id !== id));
    };

    const openPreview = (image, index) => {
        setPreviewSrc(buildImageUrl(image.id, image.fileName));
        setPreviewIndex(index);
    };
    const closePreview = () => {
        setPreviewSrc(null);
        setPreviewIndex(-1);
    };
    const deleteFromPreview = () => {
        if (previewIndex < 0) return;
        const image = (value || [])[previewIndex];
        if (!image) {
            closePreview();
            return;
        }
        handleRemove(image.id);
        closePreview();
    };

    const TILE_SIZE = 96;

    return (
        <Box
            sx={{m: 2}}
            onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
            onDrop={handleDrop}
            onPaste={handlePaste}
        >
            <Typography variant="subtitle2" color="text.secondary" sx={{mb: 1}}>
                {t("forms.transaction.images.sectionTitle")}
            </Typography>

            <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_IMAGE_MIME}
                multiple
                style={{display: "none"}}
                onChange={handleFileInputChange}
            />

            <Box
                sx={{
                    display: "flex",
                    gap: 1,
                    overflowX: "auto",
                    pb: 1,
                }}
            >
                <Box
                    onClick={triggerFilePick}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            triggerFilePick();
                        }
                    }}
                    sx={(theme) => ({
                        flex: `0 0 ${TILE_SIZE}px`,
                        height: TILE_SIZE,
                        borderRadius: "12px",
                        border: `2px dashed ${theme.palette.primary.main}`,
                        color: theme.palette.primary.main,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        userSelect: "none",
                        "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                        },
                    })}
                >
                    <AddPhotoAlternateOutlinedIcon fontSize="medium" />
                    <Typography variant="caption" sx={{mt: 0.5, lineHeight: 1.1, textAlign: "center"}}>
                        {t("forms.transaction.images.addPhoto")}
                    </Typography>
                </Box>

                {(value || []).map((image, idx) => (
                    <Box
                        key={`img_${image.id}`}
                        sx={{
                            position: "relative",
                            flex: `0 0 ${TILE_SIZE}px`,
                            height: TILE_SIZE,
                        }}
                    >
                        <Box
                            component="img"
                            src={buildImageUrl(image.id, image.fileName)}
                            alt={image.fileName || ""}
                            onClick={() => openPreview(image, idx)}
                            sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "12px",
                                border: "1px solid rgba(0,0,0,0.12)",
                                cursor: "pointer",
                                display: "block",
                            }}
                        />
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRemove(image.id);
                            }}
                            aria-label={t("common.buttons.delete")}
                            sx={{
                                position: "absolute",
                                top: 2,
                                right: 2,
                                width: 22,
                                height: 22,
                                bgcolor: "rgba(0,0,0,0.65)",
                                color: "white",
                                "&:hover": {bgcolor: "rgba(0,0,0,0.85)"},
                                p: 0,
                            }}
                        >
                            <CloseIcon sx={{fontSize: 14}} />
                        </IconButton>
                    </Box>
                ))}

                {pendingTickets.map((tkt) => (
                    <Box
                        key={tkt.ticketId}
                        sx={{
                            flex: `0 0 ${TILE_SIZE}px`,
                            height: TILE_SIZE,
                            borderRadius: "12px",
                            border: "1px solid rgba(0,0,0,0.12)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(0,0,0,0.04)",
                        }}
                    >
                        <CircularProgress size={24} />
                    </Box>
                ))}
            </Box>

            {errorMsg && (
                <Typography variant="caption" color="error" sx={{display: "block", mt: 0.5}}>
                    {errorMsg}
                </Typography>
            )}

            <ImagePreviewModal
                open={previewSrc != null}
                src={previewSrc}
                title={previewIndex >= 0 ? (value[previewIndex]?.fileName || "") : ""}
                onClose={closePreview}
                onDelete={deleteFromPreview}
            />
        </Box>
    );
};

export default TransactionImagesEditor;
